/**
 * Edit dialog for any item.
 *
 * The original pops an edit box for whatever you click — line, pad, arc, text,
 * component — with every field of that primitive (OGdlg02.cpp, the CAD
 * dialogs). This is the web equivalent: one dialog whose fields are derived
 * from the entity's kind, so every detail the model holds is editable.
 *
 * Lengths and angles are shown in the current display unit and converted back
 * on apply. Changes are committed through the caller so they land on the undo
 * stack and redraw once.
 */

import {
  type ArcEntity, type CadDocument, type ComponentEntity, type Entity,
  type FillEntity, type LineEntity, type PadEntity, type PadType,
  type SpiralEntity, type TextAnchor, type TextEntity,
  PAD_TYPE_LABELS, padIsThrough,
} from '@sircad/core/model.ts';
import {
  editValue, formatDisplay, fromDisplay, readEdit, toDisplay, type DisplayUnit,
} from '@sircad/core/units.ts';
import { padShape } from '@sircad/core/geom/pad.ts';
import { openDialog } from './dialog.ts';
import { entityPreview, padPreview, type Preview } from './preview.ts';
import { padStackView } from './stackview.ts';

export interface EditOptions {
  doc: CadDocument;
  units: DisplayUnit;
  /** Called once when changes were made and should be committed to undo. */
  onApply(label: string): void;
  /** Called when the dialog closed with no change, so a snapshot can be dropped. */
  onCancel?(): void;
  /** Screen point (e.g. the double-click) to anchor the popup near. */
  anchor?: { x: number; y: number };
  /**
   * Offer the footprint libraries for a part, and answer with what was chosen.
   *
   * The dialog has always had a Footprint box, and a box is no use to anybody
   * who does not already know what the library calls the package: the one
   * place with the suggestions and the search in it was a whole-design report,
   * which is the wrong instrument for a part you are pointing at. Supplied by
   * the shell because the libraries and the catalogue are its business; where
   * it is not supplied the box stays a box.
   *
   * Resolves to null when the picker was closed without a choice.
   */
  pickFootprint?(about: {
    part: string; ref: string; pins: number; current: string;
  }): Promise<string | null>;
}

type FieldType =
  | 'length' | 'angle' | 'number' | 'text' | 'signal' | 'footprint'
  | 'layer' | 'padtype' | 'padshape' | 'anchor' | 'bool';

interface Field {
  key: string;
  label: string;
  type: FieldType;
  /** Heading this field sits under. Fields sharing one must be adjacent. */
  section?: string;
  /**
   * For a footprint field: which part is being fitted.
   *
   * `pins` is what this entity itself carries, which is right on a board — a
   * footprint's pads are the package's pads — and short on a schematic, where
   * a 7404's U1a carries two of the package's fourteen. The caller has the
   * other sheets and corrects it; `ref` is what it corrects it by.
   */
  about?: { part: string; ref: string; pins: number };
  get(): unknown;
  set(v: unknown): void;
}

const ANCHORS: TextAnchor[] = ['BL', 'BC', 'BR', 'ML', 'MC', 'MR', 'TL', 'TC', 'TR'];
const PAD_TYPES: PadType[] = ['T0', 'T1', 'T2', 'T3', 'S0', 'S1', 'S2', 'S3'];

export function showEditDialog(entity: Entity, opts: EditOptions): void {
  const fields = fieldsFor(entity);
  const title = `Edit ${kindLabel(entity)}`;

  // The dialog edits the entity live — as the original does — so whether the
  // user hits OK or Close is immaterial once a value has changed. What decides
  // it is whether anything is *different*, compared against what every field
  // read when the dialog opened.
  //
  // Not "was a box typed in", which is what it used to be. A number arriving
  // from another program shows rounded — an Altium width is 0.0059055118110236
  // inches — so clicking into that box and out again, or retyping the same
  // figure, counted as an edit and pushed an undo step that changed nothing.
  // Worse, the rounded text could be written back over the exact value.
  // Comparing the values themselves cannot make that mistake.
  const asOpened = fields.map((f) => f.get());
  const changed = (): boolean => fields.some((f, i) => !Object.is(f.get(), asOpened[i]));
  let settled = false;

  const editOpts: EditOptions = {
    ...opts,
    // Wrap the setters so any change flips the dirty flag.
    onApply: opts.onApply,
  };

  openDialog({
    title,
    anchor: opts.anchor,
    // Never the narrow popup any more: every kind now carries a picture, and a
    // picture in a 380-pixel column is a thumbnail nobody can read.
    compact: false,
    onClose() {
      if (settled) return;
      settled = true;
      if (changed()) opts.onApply(`Edit ${kindLabel(entity)}`);
      else opts.onCancel?.();
    },
    build(body, handle) {
      const form = document.createElement('form');
      form.className = 'modal-form edit-form';
      body.append(form);

      // A picture of the thing, where there is one worth drawing. It redraws on
      // every keystroke, which is the whole point: the pad shape bug was one
      // you could only find by closing the dialog and looking at the board.
      const previews = previewsFor(entity, opts.doc);
      if (previews.length > 0) {
        form.classList.add('edit-with-preview');
        const side = document.createElement('div');
        side.className = 'edit-preview-side';
        for (const p of previews) side.append(p.el);
        form.append(side);
        const refresh = (): void => { for (const p of previews) p.update(); };
        form.addEventListener('input', refresh);
        form.addEventListener('change', refresh);
      }

      const columns = document.createElement('div');
      columns.className = 'edit-fields';
      form.append(columns);

      // Grouped under headings, as the fields group in the mind: what it is,
      // where it is, how big it is. A flat run of a dozen rows reads as a list
      // to be got through rather than as a description of an object.
      let group: HTMLElement | null = null;
      let heading = '';
      for (const f of fields) {
        const want = f.section ?? '';
        if (group === null || want !== heading) {
          heading = want;
          group = document.createElement('div');
          group.className = 'edit-group';
          if (want) {
            const h = document.createElement('div');
            h.className = 'edit-group-title';
            h.textContent = want;
            group.append(h);
          }
          columns.append(group);
        }
        group.append(renderField(f, editOpts));
      }

      const foot = document.createElement('div');
      foot.className = 'edit-foot';
      const where = document.createElement('span');
      where.className = 'edit-context';
      where.textContent = contextLine(entity, opts.doc);
      form.addEventListener('input', () => {
        where.textContent = contextLine(entity, opts.doc);
      });
      form.addEventListener('change', () => {
        where.textContent = contextLine(entity, opts.doc);
      });
      foot.append(where);

      const row = document.createElement('div');
      row.className = 'modal-buttons';
      const close = document.createElement('button');
      close.type = 'button';
      close.className = 'btn';
      close.textContent = 'Close';
      close.addEventListener('click', () => handle.close());
      const apply = document.createElement('button');
      apply.type = 'submit';
      apply.className = 'btn primary';
      apply.textContent = 'OK';
      row.append(close, apply);
      foot.append(row);
      form.append(foot);

      form.addEventListener('submit', (ev) => {
        ev.preventDefault();
        handle.close();
      });
    },
  });
}

// ---------------------------------------------------------------------------
// Field widgets
// ---------------------------------------------------------------------------

function renderField(f: Field, opts: EditOptions): HTMLElement {
  const wrap = document.createElement('label');
  wrap.className = 'field edit-field';

  const span = document.createElement('span');
  span.textContent = f.label;
  wrap.append(span);

  const control = buildControl(f, opts);
  wrap.append(control);

  // The unit, beside the box rather than only in its tooltip. Room was made
  // for this and never filled, so every length read as a bare number and the
  // only way to know whether 1.5 meant inches or millimetres was the status
  // bar at the other end of the window.
  const unit = f.type === 'length' ? opts.units : f.type === 'angle' ? '°' : '';
  if (unit) {
    const tag = document.createElement('span');
    tag.className = 'field-unit';
    tag.textContent = unit;
    wrap.append(tag);
  }
  return wrap;
}

/** What each shape is called in the dialog. */
const SHAPE_LABELS: Record<string, string> = {
  round: 'Round',
  rect: 'Rectangular',
  obround: 'Oval (stadium)',
  roundrect: 'Rounded rectangle',
  chamfered: 'Chamfered rectangle',
  trapezoid: 'Trapezoid',
  polygon: 'Polygon',
};

/**
 * A drawing of this entity, and the stack it sits in.
 *
 * Every kind gets one. The pad has a picture of its own because what matters
 * about a pad is its mask opening and its hole, neither of which the editor
 * draws on the board; everything else is drawn by the renderer itself, put
 * alone in a copy of its document, so the picture cannot disagree with what is
 * behind the dialog.
 *
 * A pad on a board gets the padstack beside it as well, showing what it is
 * actually joined to on each copper layer. Only the pad: a track is on the one
 * layer it is drawn on and there is nothing to say about that.
 */
function previewsFor(e: Entity, doc: CadDocument): Preview[] {
  const out: Preview[] = [];
  out.push(e.kind === 'pad' ? padPreview(e, doc) : entityPreview(e, doc));
  if (doc.kind === 'pcb' && e.kind === 'pad') out.push(padStackView(e, doc));
  return out;
}

/**
 * The item in its context, for the foot of the dialog.
 *
 * What the fields cannot say: which part this belongs to, which side of the
 * board it is on, what net it is joined to. KiCad puts the same line under its
 * pad dialog and it is the first thing you read when you are not certain you
 * clicked the thing you meant to.
 */
function contextLine(e: Entity, doc: CadDocument): string {
  const bits: string[] = [kindLabel(e)];

  if (e.kind === 'pad' && e.pin) bits[0] = `Pad ${e.pin}`;
  if (e.kind === 'text') bits[0] = `Text "${e.text.slice(0, 24)}"`;
  if (e.kind === 'component') bits[0] = e.name || 'Component';

  const layer = doc.layers[e.layer];
  if (layer) {
    bits.push(layer.bottom ? `${layer.name} (underside)` : layer.name);
  }
  if (e.signal) bits.push(`net ${e.signal}`);
  if (e.kind === 'component' && e.mirrored) bits.push('mirrored');
  if ('rotation' in e && e.rotation) bits.push(`turned ${round(e.rotation)}°`);

  return bits.join(' · ');
}

function buildControl(f: Field, opts: EditOptions): HTMLElement {
  switch (f.type) {
    case 'layer': {
      const sel = document.createElement('select');
      opts.doc.layers.forEach((l, i) => {
        const o = document.createElement('option');
        o.value = String(i);
        o.textContent = `${i}  ${l.name}`;
        sel.append(o);
      });
      sel.value = String(f.get() as number);
      sel.addEventListener('change', () => {
        const n = Number(sel.value);
        if (Number.isInteger(n)) f.set(n);
      });
      return sel;
    }
    case 'padtype': {
      const sel = document.createElement('select');
      for (const t of PAD_TYPES) {
        const o = document.createElement('option');
        o.value = t;
        o.textContent = `${t} — ${PAD_TYPE_LABELS[t]}`;
        sel.append(o);
      }
      sel.value = f.get() as string;
      sel.addEventListener('change', () => f.set(sel.value));
      return sel;
    }
    case 'padshape': {
      const sel = document.createElement('select');
      const now = String(f.get());
      // The three CIRCAD can name, plus whatever this pad actually is when it
      // is something richer. A rounded rectangle came in from another program
      // and there is no code for it, so it is offered as itself rather than
      // silently reported as a plain rectangle — which is what the type code
      // alone used to say.
      const kinds = ['round', 'rect', 'obround'];
      if (!kinds.includes(now)) kinds.unshift(now);
      for (const k of kinds) {
        const o = document.createElement('option');
        o.value = k;
        o.textContent = SHAPE_LABELS[k] ?? k;
        sel.append(o);
      }
      sel.value = now;
      sel.addEventListener('change', () => f.set(sel.value));
      return sel;
    }
    case 'anchor': {
      const sel = document.createElement('select');
      for (const a of ANCHORS) {
        const o = document.createElement('option');
        o.value = a;
        o.textContent = a;
        sel.append(o);
      }
      sel.value = (f.get() as string) || 'BL';
      sel.addEventListener('change', () => f.set(sel.value));
      return sel;
    }
    case 'bool': {
      const box = document.createElement('input');
      box.type = 'checkbox';
      box.checked = Boolean(f.get());
      box.addEventListener('change', () => f.set(box.checked));
      return box;
    }
    case 'text':
    case 'signal': {
      const input = document.createElement('input');
      input.type = 'text';
      input.value = String(f.get() ?? '');
      input.addEventListener('input', () => f.set(input.value));
      return input;
    }
    case 'footprint': {
      const input = document.createElement('input');
      input.type = 'text';
      input.value = String(f.get() ?? '');
      input.addEventListener('input', () => f.set(input.value));
      if (!opts.pickFootprint) return input;

      const wrap = document.createElement('div');
      wrap.className = 'field-find';
      const find = document.createElement('button');
      find.type = 'button';
      find.className = 'linkish';
      find.textContent = 'Find…';
      find.addEventListener('click', () => {
        void (async () => {
          const chosen = await opts.pickFootprint?.({
            part: f.about?.part ?? '',
            ref: f.about?.ref ?? '',
            pins: f.about?.pins ?? 0,
            current: input.value,
          });
          if (!chosen) return;
          input.value = chosen;
          // Through the event, so the setter, the preview and the context line
          // all hear about it the same way they hear about typing.
          input.dispatchEvent(new Event('input', { bubbles: true }));
        })();
      });
      wrap.append(input, find);
      return wrap;
    }
    case 'length':
    case 'angle':
    case 'number': {
      const input = document.createElement('input');
      input.type = 'number';
      // No step on a measurement.
      //
      // `step` is not only the spinner's increment — the browser refuses to
      // submit a value that is not a multiple of it, so a dialog holding one
      // could not be accepted at all: OK did nothing but pop "the two nearest
      // valid values are -0.443 and -0.442".
      //
      // It is not an edge case. Across the sample designs 424,403 of 1,770,649
      // lengths are not multiples of a thousandth of an inch — very nearly one
      // in four — because a board drawn in millimetres, or imported, has no
      // reason to land on our grid. Angles are rarer at 11 of 38,789, all of
      // them on one board, and that is still eleven parts nobody could edit.
      input.step = 'any';
      const raw = Number(f.get() ?? 0);
      // The value as it stood when the box was filled. Reading back against
      // this is what lets an untouched field come out bit-for-bit unchanged
      // instead of quantised to whatever the box could show.
      const asOpened = raw;
      input.value = f.type === 'length'
        ? editValue(raw, opts.units)
        : String(round(raw));
      const unit = f.type === 'length' ? ` ${opts.units}` : f.type === 'angle' ? '°' : '';
      input.title = f.label + unit;
      input.addEventListener('input', () => {
        if (f.type === 'length') {
          const v = readEdit(input.value, opts.units, asOpened);
          if (v !== null) f.set(v);
          return;
        }
        const v = parseFloat(input.value);
        if (!Number.isFinite(v)) return;
        f.set(v);
      });
      return input;
    }
    default: {
      const input = document.createElement('input');
      input.value = String(f.get() ?? '');
      return input;
    }
  }
}

// ---------------------------------------------------------------------------
// Field sets per kind
// ---------------------------------------------------------------------------

/**
 * The field keys a kind's dialog offers, without building any of it.
 *
 * Exported so a check can assert what every dialog contains — chiefly that all
 * of them offer the signal — without a browser to render them into.
 */
export function fieldKeysFor(e: Entity): string[] {
  return fieldsFor(e).map((f) => f.key);
}

function fieldsFor(e: Entity): Field[] {
  switch (e.kind) {
    case 'line': return lineFields(e);
    case 'arc': return arcFields(e);
    case 'pad': return padFields(e);
    case 'text': return textFields(e);
    case 'component': return componentFields(e);
    case 'fill': return fillFields(e);
    case 'spiral': return spiralFields(e);
  }
}

const common = (e: Entity): Field[] => [
  { key: 'layer', label: 'Layer', type: 'layer', get: () => e.layer, set: (v) => { e.layer = v as number; } },
];

const sig = (e: { signal?: string }): Field => ({
  key: 'signal', label: 'Signal', type: 'signal',
  get: () => e.signal ?? '',
  set: (v) => { const s = String(v).trim(); if (s) e.signal = s; else delete e.signal; },
});

function num<T extends object>(obj: T, key: keyof T & string, label: string, type: FieldType): Field {
  const rec = obj as Record<string, unknown>;
  return { key, label, type, get: () => rec[key] ?? 0, set: (v) => { rec[key] = v; } };
}

function lineFields(e: LineEntity): Field[] {
  return [
    ...common(e),
    { ...num(e, 'width', 'Width', 'length'), section: 'Shape' },
    {
      key: 'bulge', label: 'Bulge', type: 'angle', section: 'Shape',
      get: () => e.bulge ?? 0,
      set: (v) => { const n = v as number; if (n) e.bulge = n; else delete e.bulge; },
    },
    { ...num(e, 'x1', 'Start X', 'length'), section: 'Ends' },
    { ...num(e, 'y1', 'Start Y', 'length'), section: 'Ends' },
    { ...num(e, 'x2', 'End X', 'length'), section: 'Ends' },
    { ...num(e, 'y2', 'End Y', 'length'), section: 'Ends' },
    { ...sig(e), section: 'Connection' },
  ];
}

function arcFields(e: ArcEntity): Field[] {
  return [
    ...common(e),
    { ...num(e, 'width', 'Width', 'length'), section: 'Shape' },
    { ...num(e, 'radius', 'Radius', 'length'), section: 'Shape' },
    { ...num(e, 'angle', 'Start angle', 'angle'), section: 'Shape' },
    { ...num(e, 'sweep', 'Sweep', 'angle'), section: 'Shape' },
    {
      key: 'ecc', label: 'Eccentricity', type: 'number', section: 'Shape',
      get: () => e.eccentricity ?? 0,
      set: (v) => { const n = v as number; if (n) e.eccentricity = n; else delete e.eccentricity; },
    },
    { ...num(e, 'cx', 'Centre X', 'length'), section: 'Position' },
    { ...num(e, 'cy', 'Centre Y', 'length'), section: 'Position' },
    { ...sig(e), section: 'Connection' },
  ];
}

function padFields(e: PadEntity): Field[] {
  return [
    ...common(e),
    {
      key: 'type', label: 'Pad type', type: 'padtype',
      get: () => e.type,
      // The type code answers two questions at once — drilled or not, and what
      // shape — and a pad may override either with a field of its own, which
      // then wins. An override left standing is why choosing a rectangular pad
      // here used to be recorded faithfully and drawn exactly as before: the
      // stadium the board arrived with was still on the entity, still winning.
      //
      // So clear whichever half of the answer the code has just changed, and
      // leave the other alone: going from a through pad to a surface one of the
      // same shape must not throw away a rounded rectangle.
      set: (v) => {
        const next = v as PadType;
        if (next.charAt(1) !== e.type.charAt(1)) delete e.shape;
        if (padIsThrough(next) !== padIsThrough(e.type)) delete e.through;
        e.type = next;
        if (!padIsThrough(e.type)) delete e.hole;
      },
    },
    {
      key: 'shape', label: 'Pad shape', type: 'padshape', section: 'Shape',
      // The pad's real shape, which the type code can only approximate. A board
      // from elsewhere arrives with a rounded rectangle or a stadium and the
      // code has no word for either, so the dialog used to describe it as
      // whatever the code said and quietly disagree with the drawing.
      get: () => padShape(e).kind,
      set: (v) => {
        const kind = String(v);
        if (kind === padShape(e).kind) return;
        // Only the three the dialog offers can be set from here; the richer
        // ones need a parameter each and are kept, not authored.
        if (kind !== 'round' && kind !== 'rect' && kind !== 'obround') return;
        e.shape = { kind };
        // Keep the code honest about it, since that is what the file stores and
        // what everything without a `shape` field will read.
        const digit = kind === 'rect' ? '2' : kind === 'obround' ? '1' : '3';
        e.type = `${e.type.charAt(0)}${digit}` as PadType;
      },
    },
    { ...num(e, 'sizeX', 'Size X', 'length'), section: 'Shape' },
    { ...num(e, 'sizeY', 'Size Y', 'length'), section: 'Shape' },
    { ...num(e, 'rotation', 'Angle', 'angle'), section: 'Shape' },
    {
      key: 'hole', label: 'Drill', type: 'length', section: 'Hole',
      get: () => e.hole ?? 0,
      set: (v) => { const n = v as number; if (n && padIsThrough(e.type)) e.hole = n; else delete e.hole; },
    },
    { ...num(e, 'x', 'Position X', 'length'), section: 'Position' },
    { ...num(e, 'y', 'Position Y', 'length'), section: 'Position' },
    {
      key: 'pin', label: 'Pin', type: 'text', section: 'Connection',
      get: () => e.pin ?? '',
      set: (v) => { const s = String(v).trim(); if (s) e.pin = s; else delete e.pin; },
    },
    { ...sig(e), section: 'Connection' },
  ];
}

function textFields(e: TextEntity): Field[] {
  return [
    ...common(e),
    {
      key: 'text', label: 'Text', type: 'text', section: 'Lettering',
      get: () => e.text, set: (v) => { e.text = String(v); },
    },
    { ...num(e, 'height', 'Height', 'length'), section: 'Lettering' },
    {
      key: 'anchor', label: 'Anchor', type: 'anchor', section: 'Lettering',
      get: () => e.anchor ?? 'BL', set: (v) => { e.anchor = v as TextAnchor; },
    },
    {
      key: 'mirrored', label: 'Mirrored', type: 'bool', section: 'Lettering',
      // Underside lettering reads backwards through the board. Offered here
      // because a string that arrived from another program carries the flag and
      // there was previously no way to see it, let alone set it.
      get: () => e.mirrored ?? false,
      set: (v) => { if (v) e.mirrored = true; else delete e.mirrored; },
    },
    { ...num(e, 'x', 'Position X', 'length'), section: 'Position' },
    { ...num(e, 'y', 'Position Y', 'length'), section: 'Position' },
    { ...num(e, 'rotation', 'Angle', 'angle'), section: 'Position' },
    { ...sig(e), section: 'Connection' },
  ];
}

function componentFields(e: ComponentEntity): Field[] {
  const fields: Field[] = [
    ...common(e),
    { key: 'name', label: 'Name', type: 'text', get: () => e.name, set: (v) => { e.name = String(v); } },
    num(e, 'x', 'X', 'length'), num(e, 'y', 'Y', 'length'),
    num(e, 'rotation', 'Rotation', 'angle'),
    { key: 'mirrored', label: 'Mirrored', type: 'bool', get: () => e.mirrored ?? false, set: (v) => { e.mirrored = Boolean(v); } },
    // Ahead of the P parameters rather than after them: every other kind shows
    // its signal, and on a part it would otherwise sit below nine rows of BOM
    // fields where nobody would find it.
    sig(e),
  ];
  // The pattern this symbol becomes on the board.
  //
  // Here because "that one has the wrong footprint" is the commonest thing
  // anybody will want to fix about a part, and until now the only way in was
  // the Footprint Check dialog — a report you run over the whole design, to
  // change one field on one part you are already pointing at. Writing it is
  // the same single field `setFootprint` writes, so it cannot disturb a net.
  fields.push({
    key: 'footprint', label: 'Footprint', type: 'footprint',
    about: {
      part: e.name,
      ref: e.params[1] ?? '',
      pins: e.children.filter((c) => c.kind === 'pad' && c.pin).length,
    },
    get: () => e.footprint ?? '',
    set: (v) => {
      const s = String(v).trim();
      if (s) e.footprint = s; else delete e.footprint;
    },
  });
  // The BOM parameters P1..P9, which carry the reference designator and value.
  for (let i = 1; i <= 9; i++) {
    if (e.params[i] === undefined && i > 3) continue;
    fields.push({
      key: `P${i}`, label: `P${i}`, type: 'text',
      get: () => e.params[i] ?? '',
      set: (v) => { const s = String(v); if (s) e.params[i] = s; else delete e.params[i]; },
    });
  }
  return fields;
}

function fillFields(e: FillEntity): Field[] {
  return [
    ...common(e),
    num(e, 'width', 'Width', 'length'),
    sig(e),
  ];
}

function spiralFields(e: SpiralEntity): Field[] {
  return [
    ...common(e),
    num(e, 'width', 'Width', 'length'),
    num(e, 'cx', 'Center X', 'length'), num(e, 'cy', 'Center Y', 'length'),
    num(e, 'r1', 'Start radius', 'length'), num(e, 'r2', 'End radius', 'length'),
    num(e, 'a1', 'Start angle', 'angle'), num(e, 'a2', 'End angle', 'angle'),
    sig(e),
  ];
}

// ---------------------------------------------------------------------------

function kindLabel(e: Entity): string {
  switch (e.kind) {
    case 'line': return 'Line';
    case 'arc': return 'Arc';
    case 'pad': return e.type.charAt(0) === 'S' ? 'SMT Pad' : 'Pad';
    case 'text': return 'Text';
    case 'component': return e.name || 'Component';
    case 'fill': return 'Fill';
    case 'spiral': return 'Spiral';
  }
}

function round(v: number): number {
  return Math.round(v * 1e6) / 1e6;
}

/** Unit label for the caller's status message. */
export function displayValue(v: number, units: DisplayUnit): string {
  return formatDisplay(v, units);
}
